import { inject, Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject, map, Observable, publishReplay, refCount } from 'rxjs';
import {
  checkZendeskIsOpen,
  closeZendeskLauncher,
  getZendeskLauncher,
  openZendeskLauncher,
  probeForZendeskLauncherForSideDrawer,
  probeForZendeskLauncherThenClose,
} from '../../../lib/zendesk';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ViewModeService } from '@galaxy/conversation/core';

export interface HelpSideDrawerTemplateRef {
  id: string;
  templateRef: TemplateRef<any>;
}

@Injectable()
export class HelpSideDrawerTemplateRefService {
  private templateMap = new Map<string, TemplateRef<any>>();
  private openTemplate$$ = new BehaviorSubject<string>('');
  openTemplate$: Observable<HelpSideDrawerTemplateRef>;
  open$: Observable<boolean>;
  isZendeskOpen = false;

  private readonly viewModeService = inject(ViewModeService);

  constructor() {
    this.openTemplate$ = this.openTemplate$$.pipe(
      map((t) => {
        const templateRef = this.templateMap.get(t);
        const id = templateRef ? t : '';
        return {
          id: id,
          templateRef: templateRef,
        };
      }),
      publishReplay(1),
      refCount(),
    );

    this.open$ = this.openTemplate$$.pipe(map((a) => !!a));

    // Ensure the Help launcher is always hidden on app load
    const launcher = getZendeskLauncher();
    if (launcher) {
      closeZendeskLauncher();
    } else {
      probeForZendeskLauncherThenClose();
    }
  }

  registerTemplate(id: string, template: TemplateRef<any>): void {
    this.templateMap.set(id, template);
    if (this.openTemplate$$.getValue() === id) {
      this.open(id);
    }
  }

  open(id: string): void {
    window.setTimeout(() => {
      this.openTemplate$$.next(id);
      this.isZendeskOpen = checkZendeskIsOpen();

      const launcher = getZendeskLauncher();
      if (launcher) {
        closeZendeskLauncher();
      } else {
        probeForZendeskLauncherForSideDrawer();
      }
    });
  }

  close(id?: string): void {
    if (id) {
      if (this.openTemplate$$.getValue() === id) {
        this.openTemplate$$.next('');
      }
    } else {
      this.openTemplate$$.next('');
    }

    const launcher = getZendeskLauncher();
    if (!!launcher && this.isZendeskOpen) {
      openZendeskLauncher();
    }
  }

  toggle(id: string): void {
    const launcher = getZendeskLauncher();
    // side drawer should be close if id is empty
    if (!id || this.openTemplate$$.getValue() === id) {
      this.openTemplate$$.next('');
      if (!!launcher && this.isZendeskOpen) {
        openZendeskLauncher();
      }
    } else {
      // only one side drawer can be open at a time
      this.viewModeService.close();
      this.openTemplate$$.next(id);
      this.isZendeskOpen = checkZendeskIsOpen();
      if (launcher) {
        closeZendeskLauncher();
      } else {
        probeForZendeskLauncherForSideDrawer();
      }
    }
  }

  showChat(id?: string): void {
    if (id) {
      if (this.openTemplate$$.getValue() === id) {
        this.openTemplate$$.next('');
      }
    } else {
      this.openTemplate$$.next('');
    }

    const launcher = getZendeskLauncher();
    if (launcher) {
      openZendeskLauncher();
    }
  }
}
